import React from "react";
import Reveal from "@/components/Reveal";
import { BookButton, OrderButton } from "@/components/ActionButtons";
import { IMG } from "@/data/restaurant";
import { Leaf, Flame, Wine, Sparkles } from "lucide-react";

export default function Story() {
  return (
    <div data-testid="story-page" className="pt-32 pb-24 bg-[var(--infuse-cream)]">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-12 items-end">
        <Reveal className="md:col-span-7">
          <span className="gold-divider font-eyebrow text-[var(--infuse-gold)]">Our Story</span>
          <h1 className="font-display text-6xl md:text-7xl text-[var(--infuse-emerald)] mt-5 leading-[0.95]">
            Where heritage learns to <em className="not-italic text-[var(--infuse-pomegranate)]">improvise.</em>
          </h1>
          <p className="mt-7 text-[var(--infuse-ink)]/75 text-lg leading-relaxed max-w-xl">
            Infuse was born from a simple obsession: to take the soulful, generational recipes of India and reimagine them through the lens of a modern, neighbourhood restaurant.
          </p>
        </Reveal>
        <Reveal className="md:col-span-5" delay={120}>
          <img src={IMG.interior2} alt="Infuse warm interior" className="w-full h-[480px] object-cover" />
        </Reveal>
      </section>

      {/* Long form */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 mt-24 space-y-8 text-[var(--infuse-ink)]/80 text-lg leading-[1.85]">
        <Reveal>
          <p>
            Tucked away just a short turn off St Peter's Street, our 38-cover dining room feels at once intimate and lively — slate-green banquettes, warm fairy lights threaded through brass-accented arches, and the gentle roar of a live clay tandoor at the heart of our open kitchen.
          </p>
        </Reveal>
        <Reveal delay={80}>
          <p>
            Our kitchen is led by a team that has cooked in some of London's most lauded modern Indian rooms. They've brought that exacting craft back to St Albans — but stripped it of the formality. <em className="text-[var(--infuse-emerald)] font-medium">Infusion</em>, to us, means honouring the integrity of a regional dish, then letting it breathe through contemporary technique: smoke, char, lacto-fermentation, and seasonal British produce.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <p>
            Fully half of our menu is dedicated to vegetarian and vegan plates — never an afterthought, but designed alongside our meat and seafood courses with the same flavour rigour. Our drinks list mirrors this thinking: a curated cellar, signature craft cocktails, and a generous <em className="text-[var(--infuse-emerald)] font-medium">"I Am Driving"</em> mocktail selection that gets the same care as any spirit pour.
          </p>
        </Reveal>
      </section>

      {/* Pillars */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: Flame, title: "Live Tandoor", body: "Slow charred lamb chops, smoked paneer, perfectly blistered naans — straight from clay." },
            { icon: Leaf, title: "Half Plant", body: "An equal half of the menu is meticulously crafted vegetarian and vegan." },
            { icon: Wine, title: "Curated Bar", body: "Signature craft cocktails, low-intervention wines, and elegant mocktails." },
            { icon: Sparkles, title: "Modern Soul", body: "Heritage flavours, contemporary plating, neighbourhood warmth." },
          ].map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 70}>
              <div className="border-t border-[var(--infuse-gold)]/40 pt-6">
                <Icon size={26} strokeWidth={1.5} className="text-[var(--infuse-pomegranate)]" />
                <h3 className="font-display text-2xl mt-3 text-[var(--infuse-emerald)]">{title}</h3>
                <p className="text-[var(--infuse-ink)]/70 mt-2 text-sm leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 flex flex-wrap items-center justify-center gap-4">
        <BookButton testid="story-book-cta">Reserve Your Evening</BookButton>
        <OrderButton testid="story-order-cta">Order Online</OrderButton>
      </section>
    </div>
  );
}
